import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
  CircularProgress,
} from "@mui/material";
import { ComoButton } from "../Common/ComoButton";
import color from "../../Constants/colors";
import { useTranslation } from "react-i18next";

interface ConfirmationDialogProps {
  open: boolean;
  message: string;
  workspaceName: string;
  onClose: () => void;
  onConfirm: (workspaceName: string) => Promise<void>;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  message,
  workspaceName,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation("workspaces");
  const [enteredName, setEnteredName] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnteredName(event.target.value);
    setError(false);
  };

  const handleConfirm = async () => {
    if (enteredName === workspaceName) {
      setLoading(true);
      try {
        await onConfirm(enteredName);
      } finally {
        setLoading(false);
      }
    } else {
      setError(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ padding: "20px", color: color.primary_dark }}>
        <DialogContentText sx={{ color: color.primary_dark }}>
          {message}
        </DialogContentText>
        <TextField
          label={t("enterWorkspaceName")}
          variant="outlined"
          fullWidth
          value={enteredName}
          onChange={handleChange}
          error={error}
          helperText={error ? t("namesDoNotMatch") : ""}
          disabled={loading}
          sx={{
            marginTop: "15px",
            "& .MuiInputBase-input": {
              color: color.primary_dark,
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: color.accent,
              },
            },
            "& .MuiInputLabel-root": {
              color: color.primary_dark,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: color.primary_dark,
            },
            "& .css-1ruifsz-MuiFormLabel-root-MuiInputLabel-root[data-shrink='false']":
              {
                color: color.gray200,
              },
          }}
        />
      </DialogContent>
      <DialogActions>
        <ComoButton
          onClick={() => {
            onClose();
            setEnteredName("");
          }}
          disabled={loading}
          secondary
        >
          {t("table_no")}
        </ComoButton>
        <ComoButton
          onClick={() => {
            handleConfirm();
            setEnteredName("");
          }}
          disabled={enteredName !== workspaceName || loading}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: color.white }} />
          ) : (
            t("table_yes")
          )}
        </ComoButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
